import { useState, useCallback, useRef, useEffect } from "react";

const PLACE_TYPES = [
  { label: "Daycare", value: "daycare", googleType: "child_care_agency" },
  { label: "Preschool", value: "preschool", googleType: "preschool" },
  { label: "Public School", value: "school", googleType: "school" },
  { label: "Private School", value: "private_school", googleType: "school" },
  { label: "After-School", value: "after_school", googleType: "school" },
  { label: "Summer Camp", value: "summer_camp", googleType: "camp_ground" },
  { label: "Tutoring", value: "tutoring", googleType: "tutoring_center" },
];

const AGE_GROUPS = [
  { label: "Infant (0-1)", value: "infant" },
  { label: "Toddler (1-3)", value: "toddler" },
  { label: "Pre-K (3-5)", value: "prek" },
  { label: "Elementary (5-11)", value: "elementary" },
  { label: "Middle (11-14)", value: "middle" },
  { label: "High School (14-18)", value: "highschool" },
];

const US_CITIES = [
  "Atlanta, GA", "Austin, TX", "Baltimore, MD", "Boston, MA", "Charlotte, NC",
  "Chicago, IL", "Cincinnati, OH", "Cleveland, OH", "Columbus, OH", "Dallas, TX",
  "Denver, CO", "Detroit, MI", "El Paso, TX", "Fort Worth, TX", "Fresno, CA",
  "Houston, TX", "Indianapolis, IN", "Jacksonville, FL", "Kansas City, MO",
  "Las Vegas, NV", "Long Beach, CA", "Los Angeles, CA", "Louisville, KY",
  "Memphis, TN", "Mesa, AZ", "Miami, FL", "Milwaukee, WI", "Minneapolis, MN",
  "Nashville, TN", "New Orleans, LA", "New York, NY", "Newark, NJ", "Oakland, CA",
  "Oklahoma City, OK", "Omaha, NE", "Orlando, FL", "Philadelphia, PA", "Phoenix, AZ",
  "Pittsburgh, PA", "Portland, OR", "Raleigh, NC", "Sacramento, CA",
  "San Antonio, TX", "San Diego, CA", "San Francisco, CA", "San Jose, CA",
  "Seattle, WA", "St. Louis, MO", "Tampa, FL", "Tucson, AZ", "Tulsa, OK",
  "Virginia Beach, VA", "Washington, DC", "Albuquerque, NM", "Anchorage, AK",
  "Arlington, TX", "Aurora, CO", "Bakersfield, CA", "Boise, ID", "Buffalo, NY",
  "Chandler, AZ", "Chula Vista, CA", "Durham, NC", "Fort Wayne, IN",
  "Garland, TX", "Gilbert, AZ", "Glendale, AZ", "Greensboro, NC", "Henderson, NV",
  "Hialeah, FL", "Honolulu, HI", "Irving, TX", "Laredo, TX", "Lexington, KY",
  "Lincoln, NE", "Lubbock, TX", "Madison, WI", "Norfolk, VA", "North Las Vegas, NV",
  "Plano, TX", "Richmond, VA", "Riverside, CA", "Rochester, NY",
  "Salt Lake City, UT", "Scottsdale, AZ", "Shreveport, LA", "Spokane, WA",
  "Stockton, CA", "St. Paul, MN", "Winston-Salem, NC"
];

// Non-childcare keywords used to filter out false positives
const EXCLUDED_KEYWORDS = [
  "bar", "restaurant", "café", "cafe", "coffee", "tavern", "grill", "pub",
  "brewery", "lounge", "club", "hotel", "motel", "salon", "spa", "gym",
  "fitness", "yoga", "pizza", "burger", "diner", "bistro", "kitchen",
  "food", "eatery", "sushi", "taco", "bbq", "barbecue", "wing"
];

function isLikelyChildcare(place) {
  const name = (place.name || "").toLowerCase();
  const types = (place.types || []).join(" ").toLowerCase();
  for (const kw of EXCLUDED_KEYWORDS) {
    if (name.includes(kw)) return false;
  }
  // Must have at least one childcare-related type or name keyword
  const childcareNames = ["child", "daycare", "day care", "preschool", "school", "academy",
    "learning", "kid", "montessori", "nursery", "infant", "toddler", "early", "center",
    "children", "youth", "camp", "tutor", "care", "kiddie"];
  const hasChildcareName = childcareNames.some(k => name.includes(k));
  const hasChildcareType = types.includes("child_care") || types.includes("preschool") ||
    types.includes("school") || types.includes("tutoring");
  return hasChildcareName || hasChildcareType;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const Icon = ({ name, size = 16, color = "currentColor" }) => {
  const icons = {
    home: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
    search: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
    map: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>,
    pin: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
    heart: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>,
    star: <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
    external: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
    phone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2.26h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
    clock: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    sparkle: <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/></svg>,
    chevronDown: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
    list: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
    info: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
    target: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  };
  return icons[name] || null;
};

function Tip({ text }) {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", marginLeft: 5 }}>
      <button
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onClick={() => setOpen(o => !o)}
        style={{
          width: 18, height: 18, borderRadius: "50%", border: "none",
          background: open ? "var(--accent)" : "var(--chip-bg)", color: open ? "#fff" : "var(--muted)",
          fontSize: 10, fontWeight: 700, cursor: "pointer", display: "inline-flex",
          alignItems: "center", justifyContent: "center", padding: 0,
        }}>
        <Icon name="info" size={11} color={open ? "#fff" : "var(--muted)"} />
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 6px)", left: "50%", transform: "translateX(-50%)",
          zIndex: 300, background: "var(--surface-dark)", color: "#fff", borderRadius: 12,
          padding: "10px 12px", width: 220, boxShadow: "0 8px 32px rgba(108,71,255,.2)",
          fontFamily: "var(--font-body)", fontSize: 11, lineHeight: 1.7
        }}>{text}</div>
      )}
    </span>
  );
}

function StarRating({ rating, reviewCount, googleMapsUrl }) {
  if (!rating) return <span style={{ fontSize: 11, color: "var(--muted)" }}>No rating</span>;
  const full = Math.floor(rating);
  const half = rating % 1 >= 0.5;
  return (
    <span style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap" }}>
      <span style={{ display: "flex", gap: 1 }}>
        {[1, 2, 3, 4, 5].map(i => (
          <span key={i} style={{ fontSize: 13, color: i <= full ? "#f0b429" : (i === full + 1 && half) ? "#f0b429" : "#ddd", opacity: i === full + 1 && half ? 0.55 : 1 }}>★</span>
        ))}
      </span>
      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--text)" }}>{rating.toFixed(1)}</span>
      {reviewCount > 0 && (
        <a href={googleMapsUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--accent)", textDecoration: "none" }}>
          {reviewCount} reviews
        </a>
      )}
    </span>
  );
}

function PlaceCard({ place }) {
  const [expanded, setExpanded] = useState(false);
  const [description, setDescription] = useState(null);
  const [loadingDesc, setLoadingDesc] = useState(false);

  const typeLabel = PLACE_TYPES.find(t =>
    place.types && place.types.some(pt => pt.includes(t.googleType.split("_")[0]))
  );
  const label = typeLabel ? typeLabel.label : "Provider";

  const TYPE_COLORS = {
    "Daycare": { bg: "#e8f4ff", color: "#0066cc" },
    "Preschool": { bg: "#fff0f8", color: "#cc0077" },
    "Public School": { bg: "#e8fff0", color: "#007733" },
    "Private School": { bg: "#fffbe8", color: "#cc8800" },
    "After-School": { bg: "#f0e8ff", color: "#6600cc" },
    "Summer Camp": { bg: "#fff4e8", color: "#cc5500" },
    "Tutoring": { bg: "#e8fffc", color: "#007755" },
  };
  const typeStyle = TYPE_COLORS[label] || { bg: "var(--chip-bg)", color: "var(--muted)" };

  useEffect(() => {
    async function fetchDescription() {
      setLoadingDesc(true);
      try {
        const res = await fetch("/api/claude", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "analysis",
            body: {
              model: "claude-sonnet-4-5",
              max_tokens: 200,
              messages: [{
                role: "user",
                content: `Write a factual 2-sentence description of "${place.name}" as a ${label} located at ${place.address}. Focus on what type of program it likely offers, age groups served, and any distinguishing characteristics based on the name and location. Do NOT mention reviews or ratings. Be specific and informative, not generic. If it appears to be a Montessori, faith-based, STEM-focused, or specialized program, mention that.`
              }]
            }
          }),
        });
        const data = await res.json();
        const text = data.content ? data.content.map(b => b.text || "").join("") : "";
        setDescription(text.trim());
      } catch (e) {
        setDescription(null);
      } finally {
        setLoadingDesc(false);
      }
    }
    fetchDescription();
  }, [place.name, place.address, label]);

  return (
    <div style={{
      background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 18,
      padding: "18px 20px", marginBottom: 12, boxShadow: "var(--shadow-card)",
      transition: "box-shadow .2s",
    }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "var(--shadow-card-hover)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "var(--shadow-card)"}
    >
      <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
        {/* Type badge */}
        <div style={{
          width: 46, height: 46, borderRadius: 14, background: typeStyle.bg,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 800, color: typeStyle.color, flexShrink: 0,
          letterSpacing: ".02em"
        }}>
          {label.slice(0, 3).toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 5 }}>
            <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 20, background: typeStyle.bg, color: typeStyle.color, fontWeight: 700 }}>{label}</span>
            {place.isOpen === true && <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 20, background: "#e8faf0", color: "#1db96a", fontWeight: 700 }}>● Open Now</span>}
            {place.isOpen === false && <span style={{ fontSize: 10, padding: "3px 10px", borderRadius: 20, background: "#fff0f0", color: "#e05050" }}>Closed</span>}
          </div>

          <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "var(--font-display)", color: "var(--text)", lineHeight: 1.2 }}>{place.name}</div>
          <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 3, display: "flex", alignItems: "center", gap: 4 }}>
            <Icon name="pin" size={11} color="var(--accent)" />
            {place.address}
          </div>

          <div style={{ marginTop: 8 }}>
            <StarRating rating={place.rating} reviewCount={place.reviewCount} googleMapsUrl={place.googleMapsUrl} />
          </div>

          {/* Description */}
          {description && (
            <div style={{
              marginTop: 10, fontSize: 12, color: "var(--text)", lineHeight: 1.6,
              background: "var(--chip-bg)", borderRadius: 10, padding: "10px 12px",
              borderLeft: "3px solid var(--accent)"
            }}>
              {description}
            </div>
          )}
          {loadingDesc && (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 12, height: 12, border: "2px solid var(--border)", borderTop: "2px solid var(--accent)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
              <span style={{ fontSize: 11, color: "var(--muted)" }}>Loading description…</span>
            </div>
          )}

          {place.website && (
            <div style={{ marginTop: 8 }}>
              <a href={place.website} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: "var(--accent)", fontWeight: 600, textDecoration: "none", display: "flex", alignItems: "center", gap: 4 }}>
                <Icon name="external" size={11} color="var(--accent)" />
                {place.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </a>
            </div>
          )}
        </div>

        <button onClick={() => setExpanded(e => !e)} style={{
          background: "none", border: "1.5px solid var(--border)", borderRadius: 20, padding: "5px 12px",
          cursor: "pointer", fontSize: 11, color: "var(--muted)", fontFamily: "var(--font-body)",
          flexShrink: 0, display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap",
          transition: "all .15s", fontWeight: 500
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--accent)"; e.currentTarget.style.color = "var(--accent)"; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--muted)"; }}
        >
          <Icon name="clock" size={11} color="var(--muted)" />
          {expanded ? "Hide" : "Hours"}
          <Icon name="chevronDown" size={11} color="var(--muted)" />
        </button>
      </div>

      {expanded && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
          {place.hours && place.hours.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 8, fontWeight: 600 }}>Operating Hours</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "3px 16px" }}>
                {place.hours.map((h, i) => (
                  <div key={i} style={{ fontSize: 11, color: "var(--text)", padding: "3px 0" }}>{h}</div>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <a href={place.googleMapsUrl} target="_blank" rel="noreferrer" style={{
              fontSize: 11, fontWeight: 600, padding: "7px 14px", borderRadius: 20,
              background: "var(--accent)", color: "#fff", textDecoration: "none",
              display: "flex", alignItems: "center", gap: 5
            }}>
              <Icon name="map" size={12} color="#fff" /> Google Maps
            </a>
            {place.phone && (
              <a href={"tel:" + place.phone} style={{
                fontSize: 11, fontWeight: 600, padding: "7px 14px", borderRadius: 20,
                background: "#1db96a", color: "#fff", textDecoration: "none",
                display: "flex", alignItems: "center", gap: 5
              }}>
                <Icon name="phone" size={12} color="#fff" /> {place.phone}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ChildcareMap({ places, center }) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  useEffect(() => {
    if (!mapRef.current || !places.length) return;
    const lat = center ? center.lat : places[0].lat || 37.7749;
    const lng = center ? center.lng : places[0].lng || -122.4194;
    function initMap() {
      const L = window.L;
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
      const map = L.map(mapRef.current, { zoomControl: true, scrollWheelZoom: false }).setView([lat, lng], 13);
      mapInstanceRef.current = map;
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "OpenStreetMap" }).addTo(map);
      if (center) {
        const homeIcon = L.divIcon({
          html: '<div style="width:18px;height:18px;border-radius:50%;background:#e05050;border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.3)"></div>',
          className: "", iconSize: [18, 18], iconAnchor: [9, 9],
        });
        L.marker([lat, lng], { icon: homeIcon }).addTo(map).bindPopup("Your search location");
      }
      places.forEach(place => {
        if (!place.lat || !place.lng) return;
        const typeLabel = PLACE_TYPES.find(t =>
          place.types && place.types.some(pt => pt.includes(t.googleType.split("_")[0]))
        );
        const abbr = typeLabel ? typeLabel.label.slice(0, 3).toUpperCase() : "PLX";
        const icon = L.divIcon({
          html: '<div style="width:38px;height:38px;border-radius:50%;background:#6c47ff;border:3px solid #fff;display:flex;align-items:center;justify-content:center;font-family:sans-serif;font-size:9px;font-weight:800;color:#fff;box-shadow:0 4px 12px rgba(108,71,255,.3)">' + abbr + '</div>',
          className: "", iconSize: [38, 38], iconAnchor: [19, 19],
        });
        const stars = place.rating ? place.rating.toFixed(1) + " ★" : "No rating";
        L.marker([place.lat, place.lng], { icon }).addTo(map).bindPopup(
          '<div style="font-family:sans-serif;font-size:12px;min-width:180px;padding:4px">' +
          '<div style="font-weight:700;font-size:14px;margin-bottom:4px">' + place.name + '</div>' +
          '<div style="color:#888;font-size:11px;margin-bottom:5px">' + place.address + '</div>' +
          '<div style="color:#f0b429;font-weight:600;margin-bottom:4px">' + stars + (place.reviewCount ? " (" + place.reviewCount + ")" : "") + '</div>' +
          (place.isOpen === true ? '<div style="color:#1db96a;font-size:11px;margin-bottom:4px">● Open now</div>' : '') +
          '<a href="' + place.googleMapsUrl + '" target="_blank" style="color:#6c47ff;font-size:11px;font-weight:600">View on Google Maps →</a>' +
          '</div>'
        );
      });
    }
    if (!window.L) {
      const link = document.createElement("link"); link.rel = "stylesheet"; link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"; document.head.appendChild(link);
      const script = document.createElement("script"); script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"; script.onload = initMap; document.head.appendChild(script);
    } else { initMap(); }
    return () => { if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; } };
  }, [places, center]);

  return (
    <div style={{ borderRadius: 18, overflow: "hidden", border: "1.5px solid var(--border)", marginBottom: 20, boxShadow: "var(--shadow-card)" }}>
      <div style={{ background: "var(--surface)", padding: "14px 20px 12px", borderBottom: "1px solid var(--border)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: 10, letterSpacing: ".12em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 2 }}>Childcare Map</div>
          <div style={{ fontSize: 12, color: "var(--text)", fontWeight: 600 }}>{places.length} providers in this area</div>
        </div>
        <Icon name="map" size={20} color="var(--accent)" />
      </div>
      <div ref={mapRef} style={{ height: 380, width: "100%" }} />
    </div>
  );
}

function LocationInput({ value, onChange, onSearch }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const handleChange = (e) => {
    const val = e.target.value; onChange(val); setActiveIndex(-1);
    if (val.length >= 2) {
      const filtered = US_CITIES.filter(c => c.toLowerCase().includes(val.toLowerCase())).slice(0, 6);
      setSuggestions(filtered); setShowSuggestions(filtered.length > 0);
    } else { setSuggestions([]); setShowSuggestions(false); }
  };
  const handleSelect = (city) => { onChange(city); setSuggestions([]); setShowSuggestions(false); setActiveIndex(-1); };
  const handleKeyDown = (e) => {
    if (!showSuggestions) { if (e.key === "Enter") onSearch(); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, -1)); }
    else if (e.key === "Enter") { e.preventDefault(); if (activeIndex >= 0) handleSelect(suggestions[activeIndex]); else { setShowSuggestions(false); onSearch(); } }
    else if (e.key === "Escape") setShowSuggestions(false);
  };
  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
          <Icon name="pin" size={15} color="var(--accent)" />
        </span>
        <input className="txt-input" type="text" placeholder="e.g. Austin, TX or 78701 or South Congress"
          value={value} onChange={handleChange} onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          autoComplete="off" style={{ paddingLeft: 36 }} />
      </div>
      {showSuggestions && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: "var(--surface)", border: "1.5px solid var(--border)", borderTop: "none", borderRadius: "0 0 12px 12px", boxShadow: "0 8px 24px rgba(108,71,255,.1)", overflow: "hidden" }}>
          {suggestions.map((city, i) => (
            <div key={city} onMouseDown={() => handleSelect(city)} onMouseEnter={() => setActiveIndex(i)}
              style={{ padding: "10px 14px", fontSize: 12, cursor: "pointer", color: i === activeIndex ? "#fff" : "var(--text)", background: i === activeIndex ? "var(--accent)" : "transparent", borderBottom: i < suggestions.length - 1 ? "1px solid var(--border)" : "none", display: "flex", alignItems: "center", gap: 8 }}>
              <Icon name="pin" size={12} color={i === activeIndex ? "#fff" : "var(--muted)"} />
              {city}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Specific childcare name search with autocomplete from Google Places
function SpecificChildcareInput({ value, onChange, location }) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowSuggestions(false);
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    onChange(val);
    clearTimeout(debounceRef.current);
    if (val.length >= 2) {
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await fetch("/api/claude", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              type: "places",
              body: {
                location: location || "United States",
                radius: 50000,
                placeType: "child_care_agency",
                query: val + " daycare preschool childcare",
              }
            }),
          });
          const data = await res.json();
          const filtered = (data.results || []).filter(isLikelyChildcare).slice(0, 6);
          setSuggestions(filtered);
          setShowSuggestions(filtered.length > 0);
        } catch (e) {
          setSuggestions([]);
        }
      }, 350);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelect = (place) => {
    onChange(place.name);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: "relative" }}>
      <div style={{ position: "relative" }}>
        <span style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
          <Icon name="search" size={15} color="var(--accent)" />
        </span>
        <input className="txt-input" type="text"
          placeholder="e.g. Bright Horizons, La Petite Academy..."
          value={value} onChange={handleChange}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          autoComplete="off" style={{ paddingLeft: 36 }} />
      </div>
      {showSuggestions && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: "var(--surface)", border: "1.5px solid var(--border)", borderTop: "none", borderRadius: "0 0 12px 12px", boxShadow: "0 8px 24px rgba(108,71,255,.1)", overflow: "hidden" }}>
          {suggestions.map((place, i) => (
            <div key={place.id || i} onMouseDown={() => handleSelect(place)}
              style={{ padding: "10px 14px", fontSize: 12, cursor: "pointer", borderBottom: i < suggestions.length - 1 ? "1px solid var(--border)" : "none" }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--chip-bg)"}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ fontWeight: 600, color: "var(--text)" }}>{place.name}</div>
              <div style={{ fontSize: 10, color: "var(--muted)", marginTop: 1 }}>{place.address}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Spinner({ message }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 20px", gap: 16 }}>
      <div style={{ width: 40, height: 40, border: "3px solid var(--border)", borderTop: "3px solid var(--accent)", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
      <div style={{ fontSize: 11, color: "var(--muted)", letterSpacing: ".12em", textTransform: "uppercase" }}>{message}</div>
    </div>
  );
}

async function searchPlaces({ location, radius, placeTypes, ageGroups, specialNeeds, specificChildcare }) {
  const results = [];
  for (const pt of placeTypes) {
    const typeConfig = PLACE_TYPES.find(t => t.value === pt);
    if (!typeConfig) continue;
    const keyword = [
      specificChildcare || typeConfig.label,
      "licensed childcare children",
      (ageGroups.includes("infant") || ageGroups.includes("toddler")) ? "infant toddler" : "",
      specialNeeds ? "special needs inclusive" : "",
    ].filter(Boolean).join(" ");
    try {
      const res = await fetch("/api/claude", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "places", body: { location, radius, placeType: typeConfig.googleType, query: keyword } }),
      });
      const data = await res.json();
      if (data.results) results.push({ ...data, placeType: pt });
    } catch (e) {
      console.error("Places search failed:", e);
    }
  }
  return results;
}

function Pagination({ pageSize, setPageSize, page, setPage, total }) {
  const totalPages = Math.ceil(total / pageSize);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: 8 }}>
        <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>Per page:</span>
        {[10, 20, 40].map(n => (
          <button key={n} onClick={() => { setPageSize(n); setPage(1); }} style={{
            fontFamily: "var(--font-body)", fontSize: 11, padding: "5px 11px", borderRadius: 20,
            border: "1.5px solid " + (pageSize === n ? "var(--accent)" : "var(--border)"),
            background: pageSize === n ? "var(--accent)" : "var(--chip-bg)",
            color: pageSize === n ? "#fff" : "var(--muted)",
            cursor: "pointer", fontWeight: 600, transition: "all .15s",
          }}>{n}</button>
        ))}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: "auto" }}>
        <button onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          disabled={page === 1} style={{
            fontFamily: "var(--font-body)", fontSize: 11, padding: "5px 14px", borderRadius: 20,
            border: "1.5px solid var(--border)", background: "var(--chip-bg)",
            color: page === 1 ? "var(--border)" : "var(--text)", cursor: page === 1 ? "default" : "pointer",
            fontWeight: 600, display: "flex", alignItems: "center", gap: 4,
          }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Prev
        </button>
        <span style={{ fontSize: 11, color: "var(--muted)", whiteSpace: "nowrap" }}>
          Page {page} of {totalPages || 1} · {total} results
        </span>
        <button onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          disabled={page === totalPages || totalPages === 0} style={{
            fontFamily: "var(--font-body)", fontSize: 11, padding: "5px 14px", borderRadius: 20,
            border: "1.5px solid var(--border)", background: "var(--chip-bg)",
            color: (page === totalPages || totalPages === 0) ? "var(--border)" : "var(--text)",
            cursor: (page === totalPages || totalPages === 0) ? "default" : "pointer",
            fontWeight: 600, display: "flex", alignItems: "center", gap: 4,
          }}>
          Next
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      </div>
    </div>
  );
}

export default function FindChildcare() {
  const [locationInput, setLocationInput] = useState("");
  const [radius, setRadius] = useState(5);
  const [selectedTypes, setSelectedTypes] = useState(["daycare", "preschool"]);
  const [selectedAges, setSelectedAges] = useState([]);
  const [specialNeeds, setSpecialNeeds] = useState(false);
  const [children, setChildren] = useState(1);
  const [sortBy, setSortBy] = useState("rating");
  const [specificChildcare, setSpecificChildcare] = useState("");
  const [results, setResults] = useState([]);
  const [center, setCenter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeTab, setActiveTab] = useState("list");
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const toggleType = (val) => setSelectedTypes(s => s.includes(val) ? s.filter(v => v !== val) : [...s, val]);
  const toggleAge = (val) => setSelectedAges(s => s.includes(val) ? s.filter(v => v !== val) : [...s, val]);

  const handleSearch = useCallback(async () => {
    if (!locationInput.trim()) { setError("Please enter a location."); return; }
    if (!selectedTypes.length) { setError("Please select at least one provider type."); return; }
    setError(null); setLoading(true); setHasSearched(true); setResults([]); setPage(1);
    try {
      const allResults = await searchPlaces({ location: locationInput.trim(), radius, placeTypes: selectedTypes, ageGroups: selectedAges, specialNeeds, specificChildcare: specificChildcare.trim() });
      const combined = [];
      let searchCenter = null;
      allResults.forEach(r => {
        if (!searchCenter && r.center) searchCenter = r.center;
        r.results.forEach(place => {
          if (!combined.find(p => p.id === place.id) && isLikelyChildcare(place)) {
            combined.push({ ...place, placeType: r.placeType });
          }
        });
      });
      setCenter(searchCenter);
      setResults(combined);
    } catch (e) {
      setError("Search failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [locationInput, radius, selectedTypes, selectedAges, specialNeeds, specificChildcare]);

  const sorted = [...results].sort((a, b) => {
    if (sortBy === "rating") return (b.rating || 0) - (a.rating || 0);
    if (sortBy === "reviews") return (b.reviewCount || 0) - (a.reviewCount || 0);
    if (sortBy === "name") return a.name.localeCompare(b.name);
    return 0;
  });

  const pageStart = (page - 1) * pageSize;
  const visibleResults = sorted.slice(pageStart, pageStart + pageSize);

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      {/* Hero */}
      <div style={{ background: "linear-gradient(135deg, #f8f6ff 0%, #f0ebff 50%, #fce8f8 100%)", borderBottom: "1px solid var(--border)", padding: "32px 32px 28px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
            <div style={{ width: 42, height: 42, borderRadius: 14, background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(108,71,255,.3)" }}>
              <Icon name="heart" size={20} color="#fff" />
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: ".2em", color: "var(--accent)", fontWeight: 700, textTransform: "uppercase" }}>FIND CHILDCARE AND SCHOOLS</div>
              <div style={{ fontSize: 28, fontWeight: 800, fontFamily: "var(--font-display)", color: "var(--text)", lineHeight: 1.1 }}>Find Childcare</div>
            </div>
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", display: "flex", alignItems: "center", gap: 6 }}>
            Daycares, schools, tutors and more near any location
          </div>
          {hasSearched && !loading && results.length > 0 && (
            <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
              {[
                { label: "Found", value: results.length },
                { label: "Radius", value: radius + " mi" },
                { label: "Types", value: selectedTypes.length },
              ].map(p => (
                <div key={p.label} style={{ background: "var(--surface)", border: "1.5px solid var(--border)", borderRadius: 14, padding: "10px 18px", display: "flex", flexDirection: "column", alignItems: "center", minWidth: 80, boxShadow: "var(--shadow-card)" }}>
                  <span style={{ fontSize: 9, letterSpacing: ".1em", color: "var(--muted)", textTransform: "uppercase", marginBottom: 3 }}>{p.label}</span>
                  <span style={{ fontSize: 15, fontWeight: 800, color: "var(--accent)", fontFamily: "var(--font-display)" }}>{p.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "26px 24px" }}>
        <div className="card">
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 22 }}>
            <div style={{ width: 32, height: 32, borderRadius: 10, background: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Icon name="search" size={16} color="var(--accent)" />
            </div>
            <span style={{ fontSize: 14, fontWeight: 700, color: "var(--text)", fontFamily: "var(--font-display)" }}>Search</span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <div className="field-label">Address, ZIP Code, City, or Neighborhood <Tip text="Enter any US address, ZIP code, city, or neighborhood. Google Places will find nearby providers." /></div>
              <LocationInput value={locationInput} onChange={setLocationInput} onSearch={handleSearch} />
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <div className="field-label">
                Specific Childcare (optional)
                <Tip text="Type a specific daycare or school name to search near that location instead of your address." />
              </div>
              <SpecificChildcareInput value={specificChildcare} onChange={setSpecificChildcare} location={locationInput} />
              {specificChildcare && (
                <div style={{ marginTop: 6, fontSize: 11, color: "var(--accent)", display: "flex", alignItems: "center", gap: 4 }}>
                  <Icon name="target" size={12} color="var(--accent)" />
                  Housing results will be filtered within radius of this childcare
                </div>
              )}
            </div>

            <div>
              <div className="field-label">Search Radius <Tip text="How far from your location to search." /></div>
              <div style={{ fontSize: 18, fontWeight: 700, color: "var(--accent)", fontFamily: "var(--font-display)", marginBottom: 8 }}>{radius} miles</div>
              <input type="range" min={1} max={25} step={1} value={radius} onChange={e => setRadius(+e.target.value)} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: "var(--muted)", marginTop: 3 }}><span>1 mi</span><span>25 mi</span></div>
            </div>

            <div>
              <div className="field-label">Number of Children <Tip text="How many children you need care for." /></div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {[1, 2, 3, 4, "4+"].map(n => (
                  <button key={n} onClick={() => setChildren(n)} style={{
                    fontFamily: "var(--font-body)", fontSize: 12, padding: "8px 14px", borderRadius: 20,
                    border: "1.5px solid " + (children === n ? "var(--accent)" : "var(--border)"),
                    background: children === n ? "var(--accent)" : "var(--chip-bg)",
                    color: children === n ? "#fff" : "var(--muted)", cursor: "pointer", fontWeight: 600
                  }}>{n}</button>
                ))}
              </div>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <div className="field-label">Provider Type (select all that apply) <Tip text="Select the types of childcare you are looking for." /></div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {PLACE_TYPES.map(t => {
                  const active = selectedTypes.includes(t.value);
                  return (
                    <button key={t.value} onClick={() => toggleType(t.value)} style={{
                      fontFamily: "var(--font-body)", fontSize: 12, padding: "8px 16px", borderRadius: 50,
                      border: "1.5px solid " + (active ? "var(--accent)" : "var(--border)"),
                      background: active ? "var(--accent)" : "var(--chip-bg)",
                      color: active ? "#fff" : "var(--muted)", cursor: "pointer", fontWeight: active ? 600 : 400
                    }}>{t.label}</button>
                  );
                })}
              </div>
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <div className="field-label">Child Age Group (select all that apply) <Tip text="Filter providers by the age groups they serve." /></div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {AGE_GROUPS.map(a => {
                  const active = selectedAges.includes(a.value);
                  return (
                    <button key={a.value} onClick={() => toggleAge(a.value)} style={{
                      fontFamily: "var(--font-body)", fontSize: 12, padding: "8px 16px", borderRadius: 50,
                      border: "1.5px solid " + (active ? "#f59e0b" : "var(--border)"),
                      background: active ? "#f59e0b" : "var(--chip-bg)",
                      color: active ? "#fff" : "var(--muted)", cursor: "pointer", fontWeight: active ? 600 : 400
                    }}>{a.label}</button>
                  );
                })}
              </div>
            </div>

            <div style={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--chip-bg)", borderRadius: 14, cursor: "pointer" }} onClick={() => setSpecialNeeds(v => !v)}>
              <div style={{ width: 38, height: 22, borderRadius: 11, background: specialNeeds ? "var(--accent)" : "var(--border)", position: "relative", transition: "background .2s", flexShrink: 0 }}>
                <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: specialNeeds ? 19 : 3, transition: "left .2s", boxShadow: "0 1px 4px rgba(0,0,0,.15)" }} />
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: specialNeeds ? "var(--text)" : "var(--muted)" }}>Special Needs / Inclusive Programs</div>
                <div style={{ fontSize: 11, color: "var(--muted)", marginTop: 1 }}>Filters for providers with special needs support</div>
              </div>
              <Tip text="Filters for providers that offer special needs support or inclusive learning environments." />
            </div>
          </div>

          <div style={{ marginTop: 22, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <button className="search-btn" onClick={handleSearch} disabled={loading}>
              <Icon name="search" size={14} color="#fff" />
              {loading ? "Searching..." : hasSearched ? "Search Again" : "Find Childcare"}
            </button>
            {error && <span style={{ fontSize: 11, color: "#e05050", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, padding: "6px 12px" }}>{error}</span>}
          </div>
        </div>

        {loading && <div className="card"><Spinner message="Searching for licensed providers near you..." /></div>}

        {!loading && hasSearched && results.length === 0 && !error && (
          <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
            <div style={{ fontSize: 13, color: "var(--muted)" }}>No providers found. Try expanding your radius or selecting more types.</div>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", gap: 6 }}>
                {["list", "map"].map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)} style={{
                    fontFamily: "var(--font-body)", fontSize: 12, padding: "7px 18px", borderRadius: 20,
                    border: "1.5px solid " + (activeTab === tab ? "var(--accent)" : "var(--border)"),
                    background: activeTab === tab ? "var(--accent)" : "var(--chip-bg)",
                    color: activeTab === tab ? "#fff" : "var(--muted)", cursor: "pointer", fontWeight: 600,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <Icon name={tab === "list" ? "list" : "map"} size={13} color={activeTab === tab ? "#fff" : "var(--muted)"} />
                    {tab === "list" ? "List View" : "Map View"}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "var(--muted)" }}>Sort:</span>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ fontFamily: "var(--font-body)", fontSize: 12, padding: "6px 12px", border: "1.5px solid var(--border)", borderRadius: 20, background: "var(--surface)", color: "var(--text)", outline: "none", width: "auto" }}>
                  <option value="rating">Highest Rated</option>
                  <option value="reviews">Most Reviewed</option>
                  <option value="name">Name A–Z</option>
                </select>
              </div>
            </div>

            <Pagination pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} total={sorted.length} />

            {activeTab === "map" && <ChildcareMap places={sorted} center={center} />}
            {activeTab === "list" && visibleResults.map(place => <PlaceCard key={place.id} place={place} />)}

            <div style={{ textAlign: "center", padding: "16px 0", fontSize: 11, color: "var(--muted)" }}>
              Data from Google Places · {results.length} licensed providers found
            </div>
          </div>
        )}

        {!hasSearched && (
          <div className="card" style={{ textAlign: "center", padding: "60px 20px", background: "linear-gradient(135deg, var(--surface) 0%, #f8f6ff 100%)" }}>
            <div style={{ width: 64, height: 64, borderRadius: 20, background: "var(--accent-soft)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <Icon name="heart" size={28} color="var(--accent)" />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "var(--font-display)", color: "var(--text)", marginBottom: 8 }}>Find childcare near any location</div>
            <div style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.8, maxWidth: 360, margin: "0 auto" }}>
              Search by address, ZIP, city, or neighborhood. Filter by provider type, child age, radius, and special needs.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
